import { useEffect, useRef } from "react";

/**
 * Tour ACTIONS — the only way a walkthrough changes page state (open a modal,
 * switch a tab) so the next highlighted element exists.
 *
 * A page registers an action while it is mounted:
 *
 *   useTourAction("emails-open-compose", () => setComposeOpen(true));
 *
 * and a tour step names it by id (`action: "emails-open-compose"`), so the
 * step registry stays plain data.
 *
 * RULE (TOUR_AUTHORING_GUIDE.md): an action may only change UI state. It must
 * never start a write — directly, or through what it opens (auto-save on
 * mount, mark-read on open, presence join, draft creation). There is no
 * mutation guard behind this rule; the rule IS the guard.
 */
const actions = new Map();

export function registerTourAction(id, fn) {
  actions.set(id, fn);
  return () => {
    if (actions.get(id) === fn) actions.delete(id);
  };
}

export const hasTourAction = (id) => actions.has(id);

export function runTourAction(id) {
  const fn = actions.get(id);
  if (!fn) return false;
  fn();
  return true;
}

/**
 * Register `fn` under `id` for as long as the calling component is mounted.
 * The latest `fn` is always the one that runs (read through a ref), so callers
 * can pass an inline arrow without re-registering on every render.
 */
export function useTourAction(id, fn) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => registerTourAction(id, () => fnRef.current?.()), [id]);
}

// Test hook only.
export const _clearTourActions = () => actions.clear();
